import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Save, Plus, Trash2, Loader2, GripVertical } from "lucide-react";
import { toast } from "sonner";

const ICONS = ["Monitor", "Headphones", "Cloud", "TrendingUp", "Lock", "GraduationCap", "Shield", "Zap", "Globe", "Server"];
const COLORS = ["#3b82f6", "#06b6d4", "#8b5cf6", "#10b981", "#f43f5e", "#f59e0b", "#ec4899", "#14b8a6"];

const ServicesEditor = ({ content, onSave }) => {
  const [formData, setFormData] = useState({
    section_label: "",
    title: "",
    subtitle: "",
    services: []
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (content) {
      setFormData(content);
    }
  }, [content]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleServiceChange = (index, field, value) => {
    const updated = [...formData.services];
    updated[index] = { ...updated[index], [field]: value };
    setFormData({ ...formData, services: updated });
  };

  const addService = () => {
    const newService = {
      id: `service-${Date.now()}`,
      icon: "Monitor",
      title: "Nuevo Servicio",
      description: "Descripción del servicio",
      color: "#3b82f6"
    };
    setFormData({ ...formData, services: [...formData.services, newService] });
  };

  const removeService = (index) => {
    const updated = formData.services.filter((_, i) => i !== index);
    setFormData({ ...formData, services: updated });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const success = await onSave(formData);
    setSaving(false);
    if (success) {
      toast.success("Servicios actualizados correctamente");
    } else {
      toast.error("Error al guardar los cambios");
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid="services-editor"
    >
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Editar Servicios</h1>
          <p className="text-[#94a3b8]">Administra los servicios que ofreces</p>
        </div>
        <button onClick={addService} className="btn-secondary flex items-center gap-2">
          <Plus className="w-5 h-5" />
          <span>Agregar Servicio</span>
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="glass-card p-6">
          <h2 className="text-xl font-semibold text-white mb-4">Textos de la Sección</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-[#94a3b8] text-sm mb-2">Etiqueta</label>
              <input
                type="text"
                name="section_label"
                value={formData.section_label}
                onChange={handleChange}
                className="contact-input w-full"
              />
            </div>
            <div>
              <label className="block text-[#94a3b8] text-sm mb-2">Título</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                className="contact-input w-full"
              />
            </div>
          </div>
          <div className="mt-4">
            <label className="block text-[#94a3b8] text-sm mb-2">Subtítulo</label>
            <textarea
              name="subtitle"
              value={formData.subtitle}
              onChange={handleChange}
              rows={2}
              className="contact-input w-full resize-none"
            />
          </div>
        </div>

        <div className="space-y-4">
          {formData.services.map((service, index) => (
            <div key={service.id} className="glass-card p-6">
              <div className="flex items-start gap-4">
                <div className="text-[#94a3b8] cursor-move">
                  <GripVertical className="w-5 h-5" />
                </div>
                
                <div className="flex-1 grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[#94a3b8] text-sm mb-2">Título</label>
                    <input
                      type="text"
                      value={service.title}
                      onChange={(e) => handleServiceChange(index, "title", e.target.value)}
                      className="contact-input w-full"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[#94a3b8] text-sm mb-2">Icono</label>
                      <select
                        value={service.icon}
                        onChange={(e) => handleServiceChange(index, "icon", e.target.value)}
                        className="contact-input w-full"
                      >
                        {ICONS.map(icon => (
                          <option key={icon} value={icon}>{icon}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-[#94a3b8] text-sm mb-2">Color</label>
                      <select
                        value={service.color}
                        onChange={(e) => handleServiceChange(index, "color", e.target.value)}
                        className="contact-input w-full"
                      >
                        {COLORS.map(color => (
                          <option key={color} value={color} style={{ color }}>{color}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-[#94a3b8] text-sm mb-2">Descripción</label>
                    <textarea
                      value={service.description}
                      onChange={(e) => handleServiceChange(index, "description", e.target.value)}
                      rows={2}
                      className="contact-input w-full resize-none"
                    />
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => removeService(index)}
                  className="p-2 text-red-400 hover:bg-red-500/10 rounded transition-colors"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="flex justify-end">
          <button type="submit" disabled={saving} className="btn-primary flex items-center gap-2">
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
            <span>{saving ? "Guardando..." : "Guardar Cambios"}</span>
          </button>
        </div>
      </form>
    </motion.div>
  );
};

export default ServicesEditor;
